// The CELL VOCABULARY of the program editor: how one cell's text becomes a
// number, and which load axes carry which kind of value. Split out of
// ./programEdit for the 500-line cap; the draft→payload BUILD that used to sit
// here moved on to ./programEditPayload when this file hit 497.
// Imports only TYPES from ./programEdit (erased at runtime), so there is
// no runtime import cycle; ./programEdit re-exports these symbols for the
// existing import paths. Keep it free of React and any UI import.

import type { SetIntensity as SetIntensityT } from "./schemas/program.js";
import type { IntensityKind, SetRowDraft } from "./programEdit.js";
import {
  fromCanonicalKg,
  toCanonicalKg,
  SIGNED_WEIGHT_KG_MAX,
  SIGNED_WEIGHT_KG_MIN,
} from "./units.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { parseLocaleNumber } from "./parseLocaleNumber.js";

// Reps cell token: "5" (exact), or "5+" from a draft written before PE-42,
// when the trailing "+" in `reps` carried the AMRAP. The flag lives in
// `CellDraft.amrap` now: the composer's reps cell still takes a typed "+" and
// turns it into the flag (ComposerSetRow), the prescribe chip writes the flag
// itself. The token is still accepted so a boot-recovered draft keeps its floor.
export const parseRepsToken = (
  v: string,
): { reps: number; amrap: boolean } | null => {
  const m = /^\s*(\d{1,2})\s*(\+)?\s*$/.exec(v);
  if (!m) return null;
  const reps = parseInt(m[1]!, 10);
  if (!Number.isFinite(reps) || reps > 50) return null;
  return { reps, amrap: m[2] === "+" };
};

/** PE-42 — is this cell an AMRAP? THE one answer, for every reader.
 *
 *  The field wins; the legacy "+" in the text is the fallback. Structural on
 *  purpose: `CellDraft` and `BlockSetRow` both fit, and the block round trip
 *  (programBlocks ⇄ programBlocksExpand) has to give the same answer as the
 *  save path or an AMRAP survives one direction and dies in the other.
 *  ⛔ Never re-derive this inline — five copies of `?? parseRepsToken(...)`
 *  is five places for the fallback to be dropped.
 */
export const amrapOf = (cell: { amrap?: boolean; reps: string }): boolean =>
  cell.amrap ?? parseRepsToken(cell.reps)?.amrap ?? false;

export const parseFloatLoose = (v: string): number | null => {
  const n = parseLocaleNumber(v);
  return Number.isFinite(n) ? n : null;
};

// Stable machine codes for a build/validation failure. `reason` stays a
// human English string (debug only); `code` is what the UI
// maps to an i18n key (program.errCode.<code>, generic fallback) so the
// error banner never shows raw English. Mirrors the coach.errCode shape.
export type IntensityErrorCode =
  | "invalidIntensityValue"
  | "weightOutOfRange"
  | "pctOutOfRange"
  | "rpeOutOfRange"
  | "rirOutOfRange"
  | "unknownIntensityKind";

export type BuildReasonCode =
  | IntensityErrorCode
  // #336: the editor's two guards before the build, now in the build's list.
  | "programNameEmpty"
  | "sessionEmpty"
  | "sessionTooManyExercises"
  | "sessionNameEmpty"
  | "slotNoExercise"
  | "slotNoSets"
  | "slotAllWeeksInactive"
  | "rowWidthMismatch"
  | "invalidReps"
  | "invalidRepsRange"
  | "invalidSeconds"
  | "invalidPause"
  | "amrapWithTime"
  | "groupKindMixed"
  | "schemaInvalid"
  // plan (single) mode only
  | "noSession"
  | "planNameEmpty"
  | "planNoExercises";

// Build a SetIntensity from the row's kind + cell value. Returns an
// { error, code } when the cell value is missing or out of range for the
// kind, so buildPayload can flag the offending cell.
export const buildIntensity = (
  kind: IntensityKind,
  rawValue: string,
  // The unit the CELL is expressed in — i.e. the unit the draft was seeded
  // in, not whatever the profile says right now (useProgramEditor captures
  // it at seed time so the two directions can never disagree). ⛔ Required
  // (CN-26 wave 3): an optional default here is exactly the type-level
  // defect the wave removes — a forgotten lb callsite would silently scale
  // the family's prescriptions by 2.2 instead of failing to compile.
  cellUnit: UnitsPreference,
): SetIntensityT | { error: string; code: IntensityErrorCode } => {
  if (kind === "none") return { kind: "none" };
  // history is value-less like none: "use the athlete's last-logged
  // weight". No numeric cell to parse.
  if (kind === "history") return { kind: "history" };
  const n = parseFloatLoose(rawValue);
  if (n == null)
    return { error: "Invalid intensity value", code: "invalidIntensityValue" };
  if (kind === "absolute") {
    // Convert BEFORE the bounds check: the range is expressed in kg (the
    // storage unit), so checking the typed lb figure against it would
    // reject a legal 2000 lb and accept an out-of-range one.
    const kg = toCanonicalKg(n, cellUnit);
    if (kg < SIGNED_WEIGHT_KG_MIN || kg > SIGNED_WEIGHT_KG_MAX)
      return { error: "Weight out of range", code: "weightOutOfRange" };
    return { kind: "absolute", weightKg: kg };
  }
  if (kind === "percent-of-max") {
    if (n < 0 || n > 200)
      return { error: "Percentage out of range", code: "pctOutOfRange" };
    return { kind: "percent-of-max", pct: n };
  }
  if (kind === "rpe") {
    if (n < 1 || n > 10)
      return { error: "RPE must be 1..10", code: "rpeOutOfRange" };
    return { kind: "rpe", target: n };
  }
  if (kind === "rir") {
    if (n < 0 || n > 10)
      return { error: "RIR must be 0..10", code: "rirOutOfRange" };
    return { kind: "rir", target: n };
  }
  return { error: "Unknown intensity kind", code: "unknownIntensityKind" };
};

// The INVERSE of buildIntensity: a stored SetIntensity back into the pair
// the editor draft speaks — the ROW's kind and the CELL's display string.
//
// ⭐ Extracted here, next to its inverse, because the same switch was
// already written inline once (programToDraft, ./programEdit.ts) and PE-22
// K1 needed it a second time to carry the load AXIS into the logger card.
// Two copies of a conversion that must agree with buildIntensity is how the
// two directions drift: a kind added to the union would be handled on the
// way in and fall through to "" on the way out, silently blanking a cell.
//
// ⚠️ `cellUnit` mirrors buildIntensity's parameter and for the same reason —
// it is the unit the DRAFT is seeded in, not the profile's current one.
// ⛔ Required, not `= "kg"` (CN-26 wave 3): see buildIntensity above.
export const intensityToCell = (
  intensity: SetIntensityT,
  cellUnit: UnitsPreference,
): { kind: IntensityKind; intensityValue: string } => {
  if (intensity.kind === "absolute")
    return {
      kind: "absolute",
      intensityValue: String(fromCanonicalKg(intensity.weightKg, cellUnit)),
    };
  if (intensity.kind === "percent-of-max")
    return { kind: "percent-of-max", intensityValue: String(intensity.pct) };
  if (intensity.kind === "rpe" || intensity.kind === "rir")
    return { kind: intensity.kind, intensityValue: String(intensity.target) };
  // none / history are VALUELESS_KINDS: no numeric cell to render.
  return { kind: intensity.kind, intensityValue: "" };
};

// #336: the axis cell `w` of a Draft row is written in: its own when it has
// one, else the row's. Every reader that turns a cell's value into a number
// or a label asks here, so a W4 RIR 3 in a kg row never reads as 3 kg.
export const kindAt = (row: SetRowDraft, w: number): IntensityKind =>
  row.cells[w]?.kind ?? row.intensityKind;

// #336 S1: set `row` is not in round `w`, which has fewer sets. Every reader
// that counts, shows or checks the sets of a round skips it through here.
export const isCellAbsent = (row: SetRowDraft, w: number): boolean =>
  row.cells[w]?.absent === true;

/** PE-34 — does this axis carry a LOAD number the coach types?
 *
 *  ⛔ NOT the complement of VALUELESS_KINDS: rpe/rir carry a value too, but it
 *  is an effort target set by tapping the band, never a load typed into the
 *  weight field. The two sets are different questions and collapsing them
 *  would put a live kg field on a sforzo row.
 *
 *  THE one answer, because three readers ask it and they must agree: the
 *  dispatch decides whether to WRITE the typed value, the prescribe hydration
 *  whether to SHOW it, and the card whether to RENDER the field at all. Two of
 *  three updated for a new kind, and the field either writes what it cannot
 *  show or shows what it cannot write — in silence, because each site is
 *  individually correct.
 */
export const isLoadBearingKind = (kind: IntensityKind): boolean =>
  kind === "absolute" || kind === "percent-of-max";

/** Does the logger's load axis take a number? No prescription (plain logging)
 *  always does; a prescription only when its kind is load-bearing. */
export const axisHasLoadValue = (
  prescribeLoad: { kind: IntensityKind } | undefined,
): boolean => prescribeLoad === undefined || isLoadBearingKind(prescribeLoad.kind);

// Kinds that carry no numeric value: the cell renders reps only and
// buildIntensity is never consulted for them.
export const VALUELESS_KINDS: ReadonlySet<IntensityKind> = new Set([
  "none",
  "history",
]);

// The value a cell falls back to when it switches into a value-bearing kind
// with nothing in it. Each seed sits inside its own kind's valid range (RIR
// is 0..10, not kg), which is why this can't be a single constant. Both the
// program editor's setRowKind and the composer's row-kind switch read it —
// they used to carry a copy each, and a copy that drifts prices an athlete's
// set wrong.
export const seedForIntensityKind = (kind: IntensityKind): string =>
  kind === "rpe" ? "8" : kind === "rir" ? "2" : kind === "percent-of-max" ? "70" : "60";
